import { useEffect, useRef, type CSSProperties, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { Icon } from '@/components/readiness/primitives'

// Centred 512px modal following the staging "Add Discovery Source" modal (§8.6).
// Iteration 5 §8.2: focus is trapped while open and returns to the element that
// opened the modal on close; Escape, ✕, a footer button or the backdrop close it.
export function RdModal({ title, subtitle, onClose, children, footer, width = 512, labelledBy, testId, bodyClassName = 'px-6 py-4', footerClassName = '', footerStyle }: { title: ReactNode; subtitle?: ReactNode; onClose: () => void; children: ReactNode; footer?: ReactNode; width?: number; labelledBy?: string; testId?: string; bodyClassName?: string; footerClassName?: string; footerStyle?: CSSProperties }) {
  const dialog = useRef<HTMLDivElement>(null)
  const opener = useRef<HTMLElement | null>(null)
  useEffect(() => {
    opener.current = document.activeElement as HTMLElement | null
    const el = dialog.current
    const focusables = () => Array.from(el?.querySelectorAll<HTMLElement>('a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])') ?? [])
    const first = focusables().find((f) => f.getAttribute('aria-label') !== 'Close') ?? focusables()[0]
    first?.focus()
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose()
        return
      }
      if (e.key !== 'Tab' || !el) return
      const items = focusables()
      if (!items.length) return
      const i = items.indexOf(document.activeElement as HTMLElement)
      if (e.shiftKey && (i <= 0 || !el.contains(document.activeElement))) {
        e.preventDefault()
        items[items.length - 1].focus()
      } else if (!e.shiftKey && (i === items.length - 1 || !el.contains(document.activeElement))) {
        e.preventDefault()
        items[0].focus()
      }
    }
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('keydown', onKey)
      opener.current?.focus?.()
    }
  }, [onClose])
  return createPortal(
    <div className="rd fixed inset-0 z-[60] flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div ref={dialog} role="dialog" aria-modal="true" aria-labelledby={labelledBy ?? 'rd-modal-title'} className="rd-card relative flex max-h-[90vh] w-full flex-col" style={{ maxWidth: width, boxShadow: 'var(--rd-shadow-float)', border: '1px solid var(--rd-border-control)', borderRadius: 14 }} data-testid={testId}>
        <div className="flex items-start justify-between gap-3 px-6 py-4" style={{ borderBottom: '1px solid var(--rd-border-subtle)' }}>
          <div className="min-w-0">
            <h2 id={labelledBy ?? 'rd-modal-title'} className="rd-section-title">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[12.5px]" style={{ color: 'var(--rd-text-muted)' }}>{subtitle}</p>}
          </div>
          <button type="button" onClick={onClose} className="grid h-9 w-9 shrink-0 place-items-center rounded-lg" style={{ color: 'var(--rd-text-muted)' }} aria-label="Close">
            <Icon name="x" size={18} />
          </button>
        </div>
        <div className={`overflow-y-auto ${bodyClassName}`}>{children}</div>
        {footer && (
          <div className={`flex items-center justify-end gap-2 px-6 py-4 ${footerClassName}`} style={{ borderTop: '1px solid var(--rd-border-subtle)', ...footerStyle }}>
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  )
}

export function RdField({ label, htmlFor, hint, children, required }: { label: string; htmlFor: string; hint?: ReactNode; children: ReactNode; required?: boolean }) {
  return (
    <div className="mb-3">
      <label htmlFor={htmlFor} className="mb-1 block text-[12.5px] font-medium" style={{ color: 'var(--rd-text-secondary)' }}>
        {label}
        {required && <span aria-hidden="true" style={{ color: 'var(--rd-status-warn)' }}> *</span>}
      </label>
      {children}
      {hint && <p className="mt-1 text-[11.5px]" style={{ color: 'var(--rd-text-faint)' }}>{hint}</p>}
    </div>
  )
}
